import { ErrorMessage, Field, Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import {
  Alert, Button, Card, Col, Container, Form, Row, Stack
} from "react-bootstrap";
import ReactQuill from "react-quill";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import * as Yup from "yup";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { userAtom } from "../../atoms/userAtom";
import { DocumentLibrary } from "../../constants/DocumentLibraryFolderId";
import { Role, showToastSuccess, showToastError } from "../../helpers";
import { customerService, documentService, leadService } from "../../services";
import { communicationLogService } from "../../services/communicationlog.service";
import HHTField from "../../Shared/HHTField";
import { ReactSelectBootstrapStyle } from "../../styles/ReactSelectBootstrapStyle";
import { LoadingSpinner } from "../LoadingSpinner";
import ClearOppositeField from "./ClearOppositeField";
import { useAtom } from "@dbeining/react-atom";
import { OptionType } from "../FormikSelect";

export default function AddCommunication() {
  const navigate = useNavigate();
  const currentUser = useAtom(userAtom);
  const isAdmin = currentUser?.identity.roles.some(
    (role) => role.name === Role.SystemAdmin
  );
  const [alertVariant, setAlertVariant] = useState("danger");
  const [allCustomers, setAllCustomers] = useState<any[]>([]);
  const [allLeads, setAllLeads] = useState<any[]>([]);
  const quillRef = useRef<any>();

  useEffect(() => {
    customerService.getAllListItems().then((response) => {
      setAllCustomers(Array.isArray(response) ? response : []);
    });
  }, []);
  
  useEffect(() => {
    leadService.getAllListItems().then((response) => {
      setAllLeads(Array.isArray(response) ? response : []);
    });
  }, []);
  

  const FilePreview = ({ file }: any) => {
    const [expanded, setExpanded] = useState(false);
    const handleClick = () => {
      setExpanded(!expanded);
    };

    const renderPreview = () => {
      if (file.type.startsWith("image") || file.type === "application/pdf") {
        return (
          <div onClick={handleClick}>
            {file.type.startsWith("image") ? (
              expanded ? (
                <img
                  src={URL.createObjectURL(file)}
                  alt={file.name}
                  style={{ width: "100%", transition: "width 0.5s" }}
                />
              ) : (
                <button>
                  <i className="fa fa-file" style={{ fontSize: "48px" }}></i>
                  <div>{file.name}</div>
                </button>
              )
            ) : file.type === "application/pdf" ? (
              expanded ? (
                <embed
                  src={URL.createObjectURL(file)}
                  type="application/pdf"
                  style={{ width: "100%", transition: "width 0.5s" }}
                />
              ) : (
                <button>
                  <i className="fa fa-file" style={{ fontSize: "48px" }}></i>
                  <div>{file.name}</div>
                </button>
              )
            ) : (
              <div onClick={handleClick}>
                <div>{file.name}</div>
              </div>
            )}
          </div>
        );
      }
      return null;
    };

    return (
      <Card.Text style={{ cursor: "pointer" }} onClick={handleClick}>
        {renderPreview()}
      </Card.Text>
    );
  };

  const labelSpan = 3;
  const fieldSpan = 9;

  const save = async (
    values: any,
    resetForm: any,
    setSubmitting: any,
    setStatus: any
  ) => {
    const customerId = values.customerId.trim() === "" ? null : values.customerId;
    const leadId = values.leadId.trim() === "" ? null : values.leadId;

    const communicationlog = {
      id: undefined,
      customerId,
      leadId,
      date: values.date,
      from: values.from,
      to: values.to,
      files: values.files,
      subject: values.subject,
      body: values.body,
    };

    try {
      const response = await communicationLogService.create(communicationlog);
      setSubmitting(false);

      if (response.status !== "Failure") {
        const files = values.files;
        documentService
          .uploadFile(DocumentLibrary.folderId, files, response.id as string)
          .catch((rejected) => {
            setAlertVariant("Could not upload file: " + rejected);
          });
        setAlertVariant("success");
        resetForm();
        showToastSuccess("Communication Log Added Successfully");
        isAdmin
          ? navigate(`/admincommunicationlog`)
          : navigate(`/communicationlog`);
      } else {
        setAlertVariant("danger");
        showToastError(response.message);
      }
    } catch (error: any) {
      setAlertVariant("danger");
      if (error.status === 400 && error.errors) {
        Object.keys(error.errors).forEach((key) => {
          const messages = error.errors[key];
          if (Array.isArray(messages)) {
            messages.forEach((message) => showToastError(message));
          }
        });
      } else {
        showToastError("An unexpected error occurred!");
      }
      setSubmitting(false);
    }
  };

  return (
    <Container fluid>
      <div className="d-flex justify-content-between align-items-center">
        <h1 className="my-3">New Communication Log</h1>
      </div>
      <Formik
        initialValues={{
          date: new Date().toISOString().split("T")[0],
          regarding: "Customer",
          customerId: "",
          leadId: "",
          from: "",
          to: "",
          files: [],
          subject: "",
          body: "",
        }}
        validationSchema={Yup.object().shape({
          date: Yup.date().required("Date is required"),
          regarding: Yup.string().required("Regarding is required"),
          customerId: Yup.string().when("regarding", {
            is: "Customer",
            then: Yup.string().required("Customer is required"),
          }),
          leadId: Yup.string().when("regarding", {
            is: "Lead",
            then: Yup.string().required("Lead is required"),
          }),
          from: Yup.string().required("From field is required"),
          to: Yup.string().required("To field is required"),
          files: Yup.array().min(0, "Please select at least one file"),
          subject: Yup.string().required("Subject is required"),
          body: Yup.string().required("Body is required"),
        })}
        onSubmit={(
          values,
          { setStatus, setSubmitting, setFieldError, resetForm }
        ) => {
          setStatus();
          setSubmitting(false);
          save(values, resetForm, setSubmitting, setStatus);
        }}
      >
        {({
          values,
          errors,
          status,
          touched,
          isSubmitting,
          handleSubmit,
          handleChange,
          setStatus,
          setFieldValue,
        }) => (
          <Form onSubmit={handleSubmit}>
            <Stack gap={3}>
              <Form.Group as={Row} controlId="date">
                <Form.Label column xs={12} lg={12} xl={labelSpan}>
                  Date <span style={{ color: "red" }}>*</span>
                </Form.Label>
                <Col xs={12} lg={12} xl={fieldSpan}>
                  <HHTField
                    name="date"
                    type="date"
                    handleChange={handleChange}
                    values={values}
                    errors={errors}
                    touched={touched}
                  />
                </Col>
              </Form.Group>

              <ClearOppositeField />

              <Form.Group as={Row} controlId="regarding" className="mb-3">
                <Form.Label column xs={12} lg={12} xl={labelSpan}>
                  Regarding <span style={{ color: "red" }}>*</span>
                </Form.Label>
                <Col xs={12} lg={12} xl={fieldSpan}>
                  <Field as="select" name="regarding" className="form-control">
                    <option value="Customer">Customer</option>
                    <option value="Lead">Lead</option>
                  </Field>
                  <ErrorMessage name="regarding" component="div" className="invalid-feedback" />
                </Col>
              </Form.Group>

              {values.regarding === "Customer" ? (
                <Form.Group as={Row} controlId="customerId" className="mb-3">
                  <Form.Label column xs={12} lg={12} xl={labelSpan}>
                    Customer <span style={{ color: "red" }}>*</span>
                  </Form.Label>
                  <Col xs={12} lg={12} xl={fieldSpan}>
                    <Select
                      styles={ReactSelectBootstrapStyle}
                      className="stateManagedSelect"
                      onChange={(value) =>
                        setFieldValue("customerId", value == null ? "" : value.value)
                      }
                      value={
                        allCustomers.find((opt: OptionType) => opt.value === values.customerId) || null
                      }
                      isClearable
                      options={allCustomers}
                    />
                    <ErrorMessage name="customerId" component="div" className="invalid-feedback" />
                  </Col>
                </Form.Group>
              ) : (
                <Form.Group as={Row} controlId="leadId" className="mb-3">
                  <Form.Label column xs={12} lg={12} xl={labelSpan}>
                    Lead <span style={{ color: "red" }}>*</span>
                  </Form.Label>
                  <Col xs={12} lg={12} xl={fieldSpan}>
                    <Select
                      styles={ReactSelectBootstrapStyle}
                      className="stateManagedSelect"
                      onChange={(value) =>
                        setFieldValue("leadId", value == null ? "" : value.value)
                      }
                      value={
                        allLeads.find((opt: OptionType) => opt.value === values.leadId) || null
                      }
                      isClearable
                      options={allLeads}
                    />
                    <ErrorMessage name="leadId" component="div" className="invalid-feedback" />
                  </Col>
                </Form.Group>
              )}

              <Form.Group as={Row} controlId="from">
                <Form.Label column xs={12} lg={12} xl={labelSpan}>
                  From <span style={{ color: "red" }}>*</span>
                </Form.Label>
                <Col xs={12} lg={12} xl={fieldSpan}>
                  <HHTField
                    name="from"
                    type="text"
                    handleChange={handleChange}
                    values={values}
                    errors={errors}
                    touched={touched}
                  />
                </Col>
              </Form.Group>
              <Form.Group as={Row} controlId="to">
                <Form.Label column xs={12} lg={12} xl={labelSpan}>
                  To <span style={{ color: "red" }}>*</span>
                </Form.Label>
                <Col xs={12} lg={12} xl={fieldSpan}>
                  <HHTField
                    name="to"
                    type="text"
                    handleChange={handleChange}
                    values={values}
                    errors={errors}
                    touched={touched}
                  />
                </Col>
              </Form.Group>
              <Form.Group as={Row} controlId="files">
                <Form.Label column xs={12} lg={12} xl={labelSpan}>
                  Files 
                </Form.Label>
                <Col xs={12} lg={12} xl={fieldSpan}>
                  <label htmlFor="fileInput">
                    <FontAwesomeIcon
                      icon={faPlus}
                      type="button"
                      className="btn btn-primary p-2"
                    />
                  </label>
                  <input
                    type="file"
                    onChange={(event: any) =>
                      setFieldValue("files", Array.from(event.currentTarget.files))
                    }
                    id="fileInput"
                    className={`d-none form-control ${errors["files"] && touched["files"] ? "is-invalid" : ""}`}
                    multiple
                    name="files[]"
                  />
                  <ErrorMessage name="files" component="div" className="invalid-feedback" />
                  <div className="mt-2">
                    {values.files.length > 0 && (
                      <Card>
                        <Card.Header as="h5">Selected Files</Card.Header>
                        <Card.Body>
                          {values.files.map((file, index) => (
                            <FilePreview key={index} file={file} />
                          ))}
                        </Card.Body>
                      </Card>
                    )}
                  </div>
                </Col>
              </Form.Group>
              <Form.Group as={Row} controlId="subject">
                <Form.Label column xs={12} lg={12} xl={labelSpan}>
                  Subject <span style={{ color: "red" }}>*</span>
                </Form.Label>
                <Col xs={12} lg={12} xl={fieldSpan}>
                  <HHTField
                    name="subject"
                    type="text"
                    handleChange={handleChange}
                    values={values}
                    errors={errors}
                    touched={touched}
                  />
                </Col>
              </Form.Group>
              <Form.Group as={Row} controlId="body">
                <Form.Label column xs={12} lg={12} xl={labelSpan}>
                  Body <span style={{ color: "red" }}>*</span>
                </Form.Label>
                <Col xs={12} lg={12} xl={fieldSpan}>
                  <ReactQuill
                    className={`${errors["body"] && touched["body"] ? "is-invalid" : ""}`}
                    ref={quillRef}
                    value={values.body ?? ""}
                    onChange={(value) => {
                      setFieldValue("body", value);
                    }}
                  />
                  <ErrorMessage name="body" component="div" className="invalid-feedback" />
                </Col>
              </Form.Group>
              {status && (
                <Alert
                  variant={alertVariant}
                  className="mt-3"
                  onClose={() => setStatus()}
                  dismissible
                >
                  {status}
                </Alert>
              )}
              <Form.Group>
                <Stack direction="horizontal" gap={3}>
                  <div className="ms-auto">
                    <Button variant="primary" type="submit">
                      {isSubmitting ? (
                        <LoadingSpinner text="Saving..." />
                      ) : (
                        "Save"
                      )}
                    </Button>
                  </div>
                </Stack>
              </Form.Group>
            </Stack>
          </Form>
        )}
      </Formik>
    </Container>
  );
}